import {Subject} from 'rxjs';
import {retry} from '@lifeomic/attempt';
import {isEmpty} from 'lodash';

import {DiffSynchronizer} from 'framework/collaboration/DiffSynchronizer';
import {
  CE_AWARENESS_CHANGE,
  CE_ERROR,
  CE_NEW_STATE,
  CE_NEW_LOCK_STATE,
  CE_STATUS,
  CE_STATUS_MODIFICATION_EXCEPTION,
  CW_AWARENESS_UPDATE,
  CW_NETWORK_STATUS_ABORT,
  CW_NETWORK_STATUS_CONNECTED,
  CW_NETWORK_STATUS_ERROR,
  CW_STATE_CHANGE,
  CW_LOCK_CHANGE,
  CW_STATUS_MODIFICATION_EXCEPTION,
  TIMEOUTS,
} from 'framework/collaboration/types';
import {
  collaborationLog,
  generateTimeId,
  isAttachment,
} from 'framework/collaboration/collaboration.utils';

/**
 * @typedef {Object} CollaborationWorkerCallbackAction
 * @property {string} roomName - The roomname that triggered the event
 * @property {string} type - the type of event
 * @property {*} [payload] - Optional data of the room
 * @property {*} [remotes] - Optional who is in the room
 */

class CollaborationRoom {
  /**
   *
   * @param {Object} params - Constructor argument object
   * @param {string} params.roomName - the room name
   * @param {string} params.status - (optional) the status to pin the document to. If not passed, will pin to the first status recieved
   * @param {(action: CollaborationWorkerCallbackAction) => void} params.awarenessCallback - Called for awareness events including network events
   * @param {(action: CollaborationWorkerCallbackAction) => void} params.remoteUpdatedCallback - Called with data from room
   */
  constructor({roomName, status, awarenessCallback, remoteUpdatedCallback}) {
    this.instanceId = generateTimeId();
    this.shouldConnect = true;
    this.roomName = roomName;
    this.status = status;
    this.awarenessCallback = awarenessCallback;
    this.remoteUpdatedCallback = remoteUpdatedCallback;
    this.inRetry = false;

    this.firehose = new Subject();

    this.localUpdateCount = 0;
    this.diffSynchronizer = new DiffSynchronizer(
      this.firehose,
      this.roomName,
      this.status
    );

    // diffSynchronizer errors change the internal state
    // it will disconnect from the websocket, and collect changes
    // in an offline state, so try reconnecting.
    this.diffSynchronizer.fh.on(CE_ERROR, (message) => {
      collaborationLog(`ERROR on document ${this.roomName}`, message);
      if (!this.shouldConnect) {
        collaborationLog(`CE_ERROR on closed room ${this.roomName}`);
        return;
      }
      this.awarenessCallback({
        roomName: this.roomName,
        type: CW_NETWORK_STATUS_ERROR,
      });
      this.connectWithRetry();
    });

    this.diffSynchronizer.fh.on(CE_STATUS_MODIFICATION_EXCEPTION, (unexpectedStatus) => {
      collaborationLog(
        `ERROR status of ${this.roomName} modified to ${unexpectedStatus}`
      );
      if (!this.shouldConnect) {
        collaborationLog(
          `CE_STATUS_MODIFICATION_EXCEPTION on closed room ${this.roomName}`
        );
        return;
      }
      this.awarenessCallback({
        roomName: this.roomName,
        type: CW_STATUS_MODIFICATION_EXCEPTION,
        ...unexpectedStatus,
      });
    });

    this.diffSynchronizer.fh.on(CE_AWARENESS_CHANGE, ({remotes}) => {
      if (!this.shouldConnect) {
        collaborationLog(`CE_AWARENESS_CHANGE on closed room ${this.roomName}`);
        return;
      }
      this.awarenessCallback({
        roomName: this.roomName,
        type: CW_AWARENESS_UPDATE,
        remotes,
      });
    });

    this.diffSynchronizer.fh.on(CE_NEW_STATE, (newState) => {
      if (isEmpty(newState) && !isAttachment(this.roomName)) {
        collaborationLog(
          `Empty state from server for ${this.roomName}, server still fetching document`
        );
        return;
      }
      if (!this.shouldConnect) {
        collaborationLog(`CE_NEW_STATE for  ${this.roomName} on closed room`);
        return;
      }
      this.remoteUpdatedCallback({
        roomName: this.roomName,
        type: CW_STATE_CHANGE,
        payload: newState,
      });
    });

    this.diffSynchronizer.fh.on(CE_NEW_LOCK_STATE, (lockInfo) => {
      if (lockInfo.locked) {
        this.awarenessCallback({
          roomName: this.roomName,
          type: CW_STATUS_MODIFICATION_EXCEPTION,
          ...lockInfo,
        });
      }
      this.remoteUpdatedCallback({
        type: CW_LOCK_CHANGE,
        roomName: this.roomName,
        payload: lockInfo,
      });
    });
  }

  /**
   * @param {*} value the local awareness state to share with others
   */
  set localAwarenessState(value) {
    this._localAwarenessState = value;
    this.diffSynchronizer.setLocalAwarenessState(value);
  }

  async connectWithRetry() {
    if (!this.shouldConnect || this.inRetry || this.diffSynchronizer.connected) {
      return;
    }
    this.inRetry = true;

    let startTime = Date.now();
    let cr = this;

    const retryOptions = {
      delay: TIMEOUTS.retryDelay,
      maxAttempts: TIMEOUTS.retryMaxAttempts,
      initialDelay: TIMEOUTS.retryInitialDelay,
      minDelay: TIMEOUTS.retryMinDelay,
      maxDelay: TIMEOUTS.retryMaxDelay,
      factor: TIMEOUTS.retryFactor,
      timeout: TIMEOUTS.maxOfflineTime,
      jitter: true,
      initialJitter: false,
      handleError: (err, context) => {
        collaborationLog(`${cr.instanceId}: retry loop failure ${cr.roomName}`);
        if (err === CE_STATUS_MODIFICATION_EXCEPTION) {
          collaborationLog(
            `${cr.instanceId}: status modification connecting to ${cr.roomName}`
          );
          context.abort(CE_STATUS_MODIFICATION_EXCEPTION);
        }
        if (Date.now() - startTime > TIMEOUTS.maxOfflineTime) {
          collaborationLog(`${cr.instanceId}: timeout connecting to ${cr.roomName}`);
          context.abort();
        }
        if (!cr.shouldConnect) context.abort();
        // send error state to awareness callback
        cr.awarenessCallback({
          roomName: cr.roomName,
          type: CW_NETWORK_STATUS_ERROR,
        });
      },
    };
    collaborationLog(`TRACE: ${cr.instanceId}: begin retry loop to ${cr.roomName}`);

    try {
      await retry(async (context) => {
        try {
          if (!cr.shouldConnect) throw new Error('retry called on a closed room');
          cr.abortContext = context;
          // some code that returns a promise or resolved value
          collaborationLog(
            `TRACE: ${cr.instanceId}: trying to connect to ${cr.roomName}`
          );
          await cr.diffSynchronizer.connectNew({
            token: cr.token,
            localAwarenessState: cr._localAwarenessState,
          });
          if (!cr.shouldConnect) throw new Error('retry called on a closed room');
          cr.awarenessCallback({
            roomName: cr.roomName,
            type: CW_NETWORK_STATUS_CONNECTED,
          });
          cr.abortContext = null;
          cr.inRetry = false;
          collaborationLog(`TRACE: ${cr.instanceId}: connected to ${cr.roomName}`);
        } catch (err) {
          collaborationLog(`WARN: ${cr.instanceId}:, failed connection attempt `, err);
          throw err;
        }
      }, retryOptions);
    } catch (err) {
      if (!cr.shouldConnect) throw new Error('retry aborted by closed room');
      collaborationLog(
        `${cr.instanceId}:  ERROR unable to connect to room, aborting: ${err.message} `,
        err
      );
      cr.abortContext = null;
      cr.inRetry = false;
      cr.close();
      if (err !== CE_STATUS_MODIFICATION_EXCEPTION) {
        cr.awarenessCallback({
          roomName: cr.roomName,
          type: CW_NETWORK_STATUS_ABORT,
        });
        throw err;
      }
    }
  }

  close() {
    this.shouldConnect = false;
    this.diffSynchronizer.close();
    this.abortContext && this.abortContext.abort();
  }

  async localUpdated(newState) {
    this.localUpdateCount++;
    const backOfMicroqueueLineCount = 2;
    for (let i = 0; i < backOfMicroqueueLineCount; i++) {
      /* simply invoking resolve() in body of (resolve)=>{..} did not work as it did in
       * validationSaga. Adding resolve to microtask queue did.
       */
      await new Promise((resolve) => queueMicrotask(resolve));
    }
    this.localUpdateCount--;
    if (this.localUpdateCount == 0) {
      this.diffSynchronizer.localUpdated(newState);
    }
  }

  sendStateless(message) {
    this.diffSynchronizer.sendStateless(message);
  }
}

// One class to rule them(all the different websocket connections) all
export class CollaborationWorkerClass {
  constructor() {
    this.token = '';
    /**
     * @type {Object.<string, CollaborationRoom>}
     */
    this.office = {};
    this.localAwarenessState = null;
    this.awarenessCallback = () => {};
    this.remoteUpdatedCallback = null;
  }

  /**
   *
   * @param {function(CollaborationRoom, string, Object.<string, CollaborationRoom>)} callback
   */
  forEachRoom(callback) {
    const roomNames = Object.keys(this.office);
    roomNames.forEach((roomName) => {
      const room = this.office[roomName];
      if (!room || !room.shouldConnect) return;
      callback(room, roomName, this.office);
    });
  }

  _readyToEnterRoom() {
    return this.token && this.remoteUpdatedCallback;
  }

  async enterRoom(roomName, status) {
    if (!this._readyToEnterRoom()) {
      return 'Not initialized';
    }
    if (this.office[roomName] && this.office[roomName].shouldConnect) {
      return 'room entered';
    }
    let room;
    try {
      room = new CollaborationRoom({
        roomName,
        status,
        awarenessCallback: this.awarenessCallback,
        remoteUpdatedCallback: this.remoteUpdatedCallback,
      });
      this.office[roomName] = room;
      room.token = this.token;
      room.localAwarenessState = this.localAwarenessState;
      await room.connectWithRetry();
    } catch (error) {
      collaborationLog(
        `ERROR Error entering room for ${roomName}:  ${error?.message}`,
        error
      );
      // we already sent error to user from timeout code inside room
      room && room.close();
      delete this.office[roomName];
      return 'error entering room';
    }
  }

  async localUpdated(roomName, newState) {
    try {
      const room = this.office[roomName];
      room.localUpdated(newState);
    } catch (error) {
      collaborationLog(`ERROR unable to set local state ${error.message}`, error);
      throw error;
    }
  }

  async leaveRoom(roomName) {
    if (!this.office[roomName]) {
      return;
    }
    this.office[roomName].close();
    delete this.office[roomName];
  }

  async leaveAllRooms() {
    const roomNames = Object.keys(this.office);
    roomNames.forEach((roomName) => {
      this.leaveRoom(roomName);
    });
  }

  async setToken(token) {
    this.token = token;
    this.forEachRoom((room) => {
      room.token = token;
    });
  }

  async setLocalAwarenessState(localAwarenessState) {
    this.localAwarenessState = localAwarenessState;
    this.forEachRoom((room) => {
      room.localAwarenessState = localAwarenessState;
    });
  }

  /**
   *
   * @param {(action: CollaborationWorkerCallbackAction) => void} remoteUpdatedCallback - Called with data from room
   */
  async setRemoteCallback(remoteUpdatedCallback) {
    this.remoteUpdatedCallback = remoteUpdatedCallback;
    this.forEachRoom((room) => {
      room.remoteUpdatedCallback = remoteUpdatedCallback;
    });
  }

  /**
   *
   * @param {(action: CollaborationWorkerCallbackAction) => void} awarenessCallback - Called for awareness events including network events
   */
  async setAwarenessCallback(awarenessCallback) {
    collaborationLog('set awareness callback');
    this.awarenessCallback = awarenessCallback;
    this.forEachRoom((room) => {
      room.awarenessCallback = awarenessCallback;
    });
  }

  async sendStateless(roomName, message) {
    this.office[roomName]?.sendStateless(message);
  }
}

const collaborationWorker = new CollaborationWorkerClass();

export async function CollaborationWorker() {
  return collaborationWorker;
}
